import { useCallback, useEffect, useRef } from 'react';
import { EMPTY_SEARCH_ITEMS } from '../product-router/product';
import useCancellablePromiseOnUnmount from '../utils/use-cancelled-promise-on-un-mount';
import useMaxOneInflightRequest from '../utils/use-max-one-inflight-request';
import { APIStates } from './product-state-machine-types';
import { useQuery } from '../query-context';
import { hasSearchResults } from '../product-router/product/utils';
import { useGenericFilters } from '../filters/filter-context';
import { useTypedAggregatorClient } from '../aggregator-client-context';
/**
 * This decorator accepts a simple search result supplier (pre/post) and decorates it with the
 * functionality of setting the results and api states depending on the different kinds of responses
 * that can be obtained via the supplier.
 *
 * @returns a decorated function that sets the results and api in their corresponding states.
 * Note: Setting these separately in different ticks was causing the results to be stale.
 */
export var useDecorateWithResultAndAPISetState = function useDecorateWithResultAndAPISetState(_ref) {
  var setState = _ref.setState,
    searchItemSupplier = _ref.searchItemSupplier,
    setAPIState = _ref.setAPIState,
    ufoExperience = _ref.ufoExperience;
  var generateCancelledPromiseOnunmount = useCancellablePromiseOnUnmount();
  var generateMaxOneInflightRequest = useMaxOneInflightRequest();
  var _useQuery = useQuery(),
    query = _useQuery.query;
  var _useGenericFilters = useGenericFilters(),
    filterDefinitions = _useGenericFilters.filterDefinitions;
  var presentQuery = useRef(query);
  var presentFilters = useRef(filterDefinitions);
  var aggregatorClient = useTypedAggregatorClient();
  var cloudId = (aggregatorClient === null || aggregatorClient === void 0 ? void 0 : aggregatorClient.getAggregatorConfig().cloudId) || '';
  useEffect(function () {
    presentQuery.current = query;
    presentFilters.current = filterDefinitions;
  }, [query, filterDefinitions]);
  return useCallback(function (arg) {
    return new Promise(function (resolve, reject) {
      setAPIState(APIStates.Loading);
      ufoExperience.start();
      generateMaxOneInflightRequest(generateCancelledPromiseOnunmount(searchItemSupplier(arg))).then(function (response) {
        if (presentQuery.current !== query) {
          ufoExperience.abort();
          return;
        }
        ufoExperience.success({
          metadata: {
            cloudId: cloudId
          }
        });
        setState(response);
        if (hasSearchResults(response)) {
          setAPIState(APIStates.Success);
        } else {
          setAPIState(APIStates.NoResult);
        }
        resolve(response);
      }).catch(function (e) {
        if (presentQuery.current !== query) {
          ufoExperience.abort();
          return;
        }
        /**
         * Set state only if the promise was not cancelled, which happens only to avoid multiple calls going out.
         */
        if (e && !e.isCancelled) {
          setState(EMPTY_SEARCH_ITEMS);
          setAPIState(APIStates.Error);
          ufoExperience.failure();
          reject(e);
        } else if (e.isCancelled) {
          ufoExperience.abort();
        }
      });
    });
  }, [searchItemSupplier, setState, generateCancelledPromiseOnunmount, setAPIState, generateMaxOneInflightRequest, query, ufoExperience, cloudId]);
};